import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import DialogComponentWrapper from "../Utils/DialogComponentWrapper";
import PromiseWrapper from "../Utils/PromiseDialog";
import { NodeForm } from "../Node/Node";
import { showPath, ZoneForm } from "../Zone/Zone";

import { primeUpdate, TREE_TABLE, findNodeAndChildren, DATA_TABLE } from "../../utils/DataUtils";
import switchTipoNodo from "../../utils/switchTipoNodo";
import { dateDiffInMinutes, dateFormat } from "../../utils/date";
import { useTreegridColsEffect } from "../../utils/tables";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle, faEye, faUndo, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FilterMatchMode } from "primereact/api";
import RestAPI from "@aws-amplify/api-rest";
import { FarmContext } from "../../contexts/FarmContext";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";

import TreeTablePrime from "../PrimeComponents/TreeTablePrime";
import DataTablePrime from "../PrimeComponents/DataTablePrime";
import BulkEditBtnPrime from "../PrimeComponents/BulkEditBtnPrime";
import TooltipPrime from "../PrimeComponents/TooltipPrime";
import DropdownPrime from "../PrimeComponents/DropdownPrime";


const getTypes = (res, { tipo, one_block }) => {
    let has2or3 = false;
    res[0] = (res[0] || 0) + 1;

    tipo && tipo.forEach((t) => {
        if (!t) return;
        res[t] = (res[t] || 0) + 1;
        if (!has2or3 && (t === 2 || t === 3)) {
            has2or3 = true;
            res[6] = (res[6] || 0) + 1;
        }
        if (t === 3 && !one_block) res[7] = (res[7] || 0) + 1;
    });
};

const customAttributes = { class: "customcss" };

const ZonesTable = ({ dataFarm, dispatch, canEdit, sectorsInfo, equiposInfo, hidraInfo, isEquipo, isFarmInfo, isHidra, isPump,isPressure,  t, i18n, units }) => {
    const [dialogState, setDialogState] = useState({ visible: false });
    /* When its not false, this holds an (sparse) array with the list/count of sector types.
   * If we have 4 selected sectors with types [1,3], [1], [2] and [2,3], this array will be:
   * showDDB: [4, 2, 2, 2, undefined, undefined, 3].
   *  This means: 4 sectors, 2 with type 1, 2 with type 2, and 2 with type 3. Also, the index 6 holds "2 or 3"
   */
    const navigate = useNavigate();
    const [showDDB, setDropDown] = useState(false);
    const formikRef = useRef(false);
    const updatingRef = useRef(false);
    const treeGrid = useRef(null);
    const promiseWrapper = useRef(null);
    const closeEstadosRef = useRef(null);
    const [sectorsTable, setSectorsTable] = useState(null);
    const [selectedNodes, setSelectedNodes] = useState(null);
    const awsOptions = useRef({
        promiseWrapper, t, dispatch, path: "sectors",
        canModify: (isHidra && isPump) ? ["dato_consigna", "grupo_variador_id", "tipo_setpoint", "porcentaje", "setpoint"] : ["nombre", "nombre_alternativo", "superficie",
            "cultivo_especie_id", "cultivo_variedad_id", "sub_especie", "caudal_teorico", "kc",
            "maximo", "punto_critico_1", "punto_critico_2", "punto_critico_3", "punto_critico_4", "minimo",
            "calor_limite", "punto_gestion_1", "punto_gestion_2", "helada_limite", "plantilla_estado_fenologico_id"
        ], closeEstadosRef, sectorsInfo
    });

    const campo_id = dataFarm && dataFarm.campo_id;
    const url = isHidra && isPump ? `/farms/${campo_id}/grupo_variador` : `/farms/${campo_id}/sector`;
    if (awsOptions.current.areaInfo !== dataFarm.areaInfo) awsOptions.current.areaInfo = dataFarm.areaInfo;
    if (awsOptions.current.url !== url) awsOptions.current.url = url;
    if (isHidra && isPump) {
        awsOptions.current.hidraInfo = hidraInfo;
        awsOptions.current.sectorsInfo = sectorsInfo;
    }

    const selectItems = useMemo(() => {
        const items = isEquipo ? [
            { id: "cultivo_especie_id,cultivo_variedad_id,sub_especie", text: t("farms.zonesTable.crop") },
            { id: "caudal_teorico", text: t("farms.zone.caudalTeorico") },
            { id: "kc", text: t("farms.zone.kc") }
        ] : (isHidra && isPump) ? [{ id: "porcentaje,setpoint,tipo_setpoint", text: t("farms.zone.datoConsigna") }] : [
            { id: "superficie,superficie_dividida", text: t("area.area") },
            { id: "cultivo_especie_id,cultivo_variedad_id,sub_especie", text: t("farms.zonesTable.crop") },
            { id: "caudal_teorico", text: t("farms.zone.caudalTeorico") },
            { id: "kc", text: t("farms.zone.kc") },
            { id: "maximo,minimo,punto_critico_1,punto_critico_2,punto_critico_3,punto_critico_4", text: t("farms.zone.parametrosSuelo") },
            { id: "calor_limite,helada_limite,punto_gestion_1,punto_gestion_2", text: t("farms.zone.parametrosClima") },
            ...(dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado ? [{ id: "plantilla_estado_fenologico_id", text: t("farms.fenologia.plantilla")}] : [])
        ];
        if (!Array.isArray(showDDB)) return items;

        /* Process showDDB to get the "disabled" and "full" states.
     * Disabled is the negate union of the states, while "full" is the intersection
     */
        const allN = showDDB[0];
        const { tipos, full } = showDDB.reduce((res, n, index) => {
            if (n) res.tipos.push(index);
            if (n === allN) res.full.push(index);
            return res;
        }, { tipos: [], full: [] });
        const no3WithMultiBlock = tipos.indexOf(7) < 0;
        const allAre3WithMultiBlock = full.indexOf(7) > -1;

        items.forEach((item) => {
            if (item.id === "superficie") {
                item.disabled = allAre3WithMultiBlock;
                item.full = no3WithMultiBlock;
            } else {
                const allowList = item.id.split(",");
                item.disabled = !allowList.some((path) => showPath(tipos, path));
                item.full = allowList.some((path) => showPath(full, path));
            }
        });

        return items;
    }, [t, showDDB, dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado]);

    const names = useMemo(() => [
        "ID", t("general.name"), t("farms.pump.pump"),
        t("general.type"), t("area.area"),
        t("farms.zonesTable.crop"), t("farms.zone.caudalTeorico"), t("farms.zone.datoConsigna"), t("farms.fenologia.plantilla")
    ], [t, dataFarm, isEquipo]);

    useEffect(() => {
        const equipos = {};
        sectorsInfo.forEach(e => {
            if(!equipos[e?.equipo?.[0]??"default"]){
                equipos[e?.equipo?.[0]??"default"] = [];
            }
            e.equipoGroup = e.equipo[0];
            equipos[e?.equipo?.[0]??"default"].push({nombre:e.nombre, orden:e.orden});
        });
        sectorsInfo?.sort((a,b) => {
            if(a?.equipoGroup == b?.equipoGroup){
                if(a?.orden > b?.orden) return 1;
                else if(a?.orden < b?.orden) return -1;
                else return 0;
            }
            else
                if(!a?.equipoGroup) return 1;
            if(!b?.equipoGroup) return -1;
            if(equiposInfo?.[a.equipoGroup]?.equipo_orden > equiposInfo?.[b.equipoGroup]?.equipo_orden) return 1;
            else if(equiposInfo?.[a.equipoGroup]?.equipo_orden < equiposInfo?.[b.equipoGroup]?.equipo_orden) return -1;
            else return 0;
        });
        setSectorsTable(sectorsInfo);
    }, [equiposInfo, t, dataFarm.fenologia, campo_id]);

    const templateSuperficie = (rowData, kilo_unidad) =>{
        return  t("general.number_unit", { value: { number: rowData.superficie, unit: kilo_unidad } }) || "-";
    };
    const templateCaudalFormateado = (rowData, cteorico_unidad) =>{
        return  t("general.number_unit", { value: { number: rowData.caudal_teorico, unit: cteorico_unidad } }) || "-";
    };
    const templateCultivos = (rowData) => {
        const cultivosAntiguos = (Array.isArray(rowData.cultivos) && rowData.cultivos.length > 0) ? rowData.cultivos.join(", ") : "";
        const cultivosNuevos = rowData.especie ? (rowData.especie[i18n.language] ?? rowData.especie["en"]) : "-";
        //sector.cultivos_traducidos = (cultivosAntiguos !== "") ? (cultivosNuevos !== "-" ? (`${cultivosAntiguos}, ${cultivosNuevos}`) : cultivosAntiguos) : cultivosNuevos;
        return (cultivosNuevos !== "-" ? cultivosNuevos : (cultivosAntiguos !== "") ? cultivosAntiguos : "-");

    };
    const templateTipoSector = (rowData) => {
        return (Array.isArray(rowData.tipo) && rowData.tipo[0]) ? (rowData.tipo.map((ts) => ts && t(`tipo.sector.${ts}`)).join(", ")) : "-";
    };
    const templatePlantillaFenologia = (rowData, dataFarm) => {
        return dataFarm.fenologia?.find(p => p.plantilla_estado_fenologico_id === rowData.plantilla_estado_fenologico_id)?.nombre || "-";
    };
    const templateEquiposAgregados = (rowData, equiposInfo) => {
        if (equiposInfo) {
            let equipos = "-";
            (Array.isArray(rowData.equipo) && rowData.equipo[0]) && rowData.equipo.forEach(e => {
                equipos = (equipos === "-") ? (equiposInfo[e] ? equiposInfo[e].nombre : "-") : (`${equipos}, ${equiposInfo[e] ? equiposInfo[e].nombre : "-"}`);
            });
            if (equipos === "-") return <span> {equipos} </span>;
            return (
                <a className="pointer"
                    onClick={() => {
                        const { campo_id, navigate } = templatesDataPointer.current;
                        navigate({ pathname: "/farms/pumpsys", search: `?farm=${campo_id}&pumpsys=${rowData.equipo[0]}` },
                            { state: { expanded: ["equipos",`equipo-${rowData.equipo[0]}`], updateTree: true } });
                    }} rel="noopener noreferrer"
                >
                    {equipos}
                </a>);
        }else{
            if (!rowData.equipo || !rowData.equipo[0]) return "-";
        }
    };
    /* we use useMemo instead of useEffect so its run before useAmplifyTreeGridRef */
    const beforeDataBound = useMemo(() => ({ result: sectors }) => {
        const cteorico_unidad = units && units.cteorico_unidad;
        const kilo_unidad = dataFarm.areaInfo && dataFarm.areaInfo.kilo_unidad;
        Array.isArray(sectors) && sectors.forEach((sector) => {
            if (sector) {
                const cultivosAntiguos = (Array.isArray(sector.cultivos) && sector.cultivos.length > 0) ? sector.cultivos.join(", ") : "";
                const cultivosNuevos = sector.especie ? (sector.especie[i18n.language] ?? sector.especie["en"]) : "-";
                //sector.cultivos_traducidos = (cultivosAntiguos !== "") ? (cultivosNuevos !== "-" ? (`${cultivosAntiguos}, ${cultivosNuevos}`) : cultivosAntiguos) : cultivosNuevos;
                sector.cultivos_traducidos = (cultivosNuevos !== "-" ? cultivosNuevos : (cultivosAntiguos !== "") ? cultivosAntiguos : "-");

                if (equiposInfo) {
                    let equipos = "-";
                    (Array.isArray(sector.equipo) && sector.equipo[0]) && sector.equipo.forEach(e => {
                        equipos = (equipos === "-") ? (equiposInfo[e] ? equiposInfo[e].nombre : "-") : (`${equipos}, ${equiposInfo[e] ? equiposInfo[e].nombre : "-"}`);
                    });
                    sector.equipos_agregados = equipos;
                }
                sector.tipo_sector = (Array.isArray(sector.tipo) && sector.tipo[0]) ? (sector.tipo.map((ts) => ts && t(`tipo.sector.${ts}`)).join(", ")) : "-";
                sector.caudal_formateado = t("general.number_unit", { value: { number: sector.caudal_teorico, unit: cteorico_unidad } }) || "-";
                sector.superficie_formateada = t("general.number_unit", { value: { number: sector.superficie, unit: kilo_unidad } }) || "-";
                sector.plantilla_fenologia = dataFarm.fenologia?.find(p => p.plantilla_estado_fenologico_id === sector.plantilla_estado_fenologico_id)?.nombre || "-";
            }
        });
    }, [equiposInfo, t, dataFarm.fenologia]);

    const [loadingTable, setLoadingTable] = useState(false);

    const templatesDataPointer = useRef({});
    const templateData = templatesDataPointer.current;
    if (templateData.campo_id !== campo_id) templateData.campo_id = campo_id;
    if (templateData.navigate !== navigate) templateData.navigate = navigate;
    if (templateData.isEquipo !== isEquipo) templateData.isEquipo = isEquipo;

    // eslint-disable-next-line react/display-name
    const templateRedirectToZone = useMemo(() => ({ nombre, nombre_alternativo, sector_id }) => {
        if (!sector_id) return null;
        const { isEquipo } = templatesDataPointer.current;
        return (
            <a className="pointer" onClick={() => {
                const { campo_id, navigate } = templatesDataPointer.current;
                navigate({ pathname: "/farms/zone", search: `?farm=${campo_id}&zone=${sector_id}` },
                    { state: { expanded: ["sectores",`sector-${sector_id}`], updateTree: true } });
            }} rel="noopener noreferrer"
            >
                {(isEquipo && nombre_alternativo) || nombre}
            </a>
        );
    }, []);
    if (!Array.isArray(sectorsInfo)) return null;

    const heightTable = Array.isArray(sectorsInfo) &&
    (sectorsInfo.length < 10 ? `${sectorsInfo.length * 100}px` : "500px");
    const template_dato_consigna = (rowData) => {
        if(rowData?.tipo_setpoint === "PRESSURE"){
            return rowData.setpoint +" bar";
        }
        return rowData.porcentaje + "%";
    };
    return (<div>
        <PromiseWrapper
            ref={promiseWrapper}
            yesText={t("general.answerYes")} noText={t("general.answerNo")}
            headerText={t("zone.recalcHeader")} contentText={t("zone.recalcBody")}
        />
        <PromiseWrapper
            ref={closeEstadosRef}
            yesText={t("forms.save")} noText={t("forms.cancel")}
            headerText={t("zone.closeEstadosHeader")} contentText={t("zone.closeEstadosBody")}
        />
        {canEdit ? <div>
            <DialogComponentWrapper formikRef={formikRef} containerId="zones-table-form"
                t={t} dialogState={dialogState} setDialogState={setDialogState}
            >
                <ZoneForm
                    isPressure={isPressure}
                    innerRef={formikRef}
                    canEdit
                    edit
                    isTableDialog
                    alert={dialogState.alert}
                    allowList={dialogState.allowList}
                    farmId={dataFarm.campo_id}
                    data={dialogState.data}
                    areaInfo={{ unidad: dataFarm.areaInfo && dataFarm.areaInfo.kilo_unidad || "" }}
                    hidraInfo={hidraInfo}
                    updateData={(data, previousData, key) => primeUpdate(key, data, previousData,awsOptions, sectorsTable, setSectorsTable, DATA_TABLE, setDialogState, setLoadingTable, dialogState, null, selectedNodes)}
                    units={units}
                    onlyDatoConsigna={isHidra && isPump}
                    rolloutFenologia={dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado}
                    plantillasFenologia={dataFarm?.fenologia}
                    selectedRecords={treeGrid?.current?.getSelectedRecords()}
                />
            </DialogComponentWrapper>
            <BulkEditBtnPrime divId="zonesTableToolbar" enabled={Boolean(showDDB)} items={selectItems} openDialog={setDialogState} headerText={t("forms.bulk")} />

        </div> : null}

        <DataTablePrime
            t={t}
            loading={loadingTable}
            items={sectorsTable}
            scrollHeight={heightTable}
            selectionMode={canEdit?"checkbox":null}
            selection={canEdit?selectedNodes:null}
            paginator = {sectorsTable?.length > 5 ? true:null}
            rows={30}
            filters={{
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            }}
            size="small"
            globalFilterFields={
                isEquipo ? ["nombre", "nombre_alternativo", "cultivos_traducidos", "caudal_formateado", "superficie_formateada"] :
                    isFarmInfo ? ["nombre", "cultivos_traducidos", "equipos_agregados", "tipo_sector", "superficie_formateada"] :
                        isHidra ? isPump ? ["nombre", "caudal_formateado", "tipo_sector", "superficie_formateada", "cultivos_traducidos", "dato_consigna"] : ["nombre", "caudal_formateado", "tipo_sector", "superficie_formateada", "cultivos_traducidos"] :
                            ["nombre", "equipos_agregados", "tipo_sector"]
            }
            rowsPerPageOptions={sectorsTable?.length > 5 ? [10, 25, 50]:null}
            header={sectorsTable?.length > 5 ? "search":null}
            filterMode="strict"
            onSelectionChange={
                (e) => {
                    if(canEdit){
                        if (!Array.isArray(e.value) || !e.value.length) {
                            setDropDown(false);
                            setSelectedNodes(e.value);
                            return;
                        }
                        const newTypes = [];
                        if(e.value.length < selectedNodes?.length){
                            const allTypes = e.value.reduce((res, data) => {
                                getTypes(res, data);
                                return res;
                            }, []);
                            setDropDown(allTypes);
                        }
                        else{
                            e.value.forEach((d) => getTypes(newTypes, d));
                            setDropDown((currentTypes) => {
                                if (!currentTypes || !currentTypes.length) return newTypes;
                                newTypes.forEach((number, index) => {
                                    if (!number) return;
                                    currentTypes[index] = (currentTypes[index] || 0) + number;
                                });
                                return currentTypes.splice(0, currentTypes.length);
                            });

                        }
                        setSelectedNodes(e.value);
                    }
                }
            }
            editableRow={canEdit}
            editFunction= {canEdit? (event, rowData) => setDialogState({ target:event.target, data: rowData, headerText: t("forms.edit"), visible: true }):null}
            fields={[
                {field:"sector_id",header:names[0], hidden:true},
                {field:"equipo",header:names[2], body:(e) =>templateEquiposAgregados(e, equiposInfo), hidden:!(!isEquipo && !isHidra), sortable:true},
                {field:(isEquipo?"nombre_alternativo":"nombre"),header:names[1], body:templateRedirectToZone,bodyStyle:{textAlign:"left"}, expander:true, sortable:true},
                {field:"caudal",header:names[6], body:(e) => templateCaudalFormateado(e, units && units.cteorico_unidad), hidden:!(isEquipo || isHidra), sortable:true},
                {field:"tipo",header:names[3], body:templateTipoSector, hidden:!(!isEquipo), sortable:true},
                {field:"superficie",header:names[4], body:(e) => templateSuperficie(e, dataFarm.areaInfo && dataFarm.areaInfo.kilo_unidad),hidden:!(isFarmInfo || isEquipo || isHidra), sortable:true},
                {field:"cultivos",header:names[5],body:templateCultivos, hidden:!(isFarmInfo || isEquipo || isHidra), sortable:true},
                {field:"dato_consigna",header:names[7], hidden:!(isHidra && isPump), body:template_dato_consigna, sortable:true},
                {field:"plantilla_fenologia",body: (e) => templatePlantillaFenologia(e, dataFarm), header:names[8], hidden:!(dataFarm?.features_dropcontrol?.fenologia?.esta_habilitado && (isFarmInfo || isEquipo || isHidra)), sortable:true},
            ]}
        />
    </div>);
};

const ZonesMapTableRow = ({ dataFarm, data, poligonosList, update, center, fileUploaded }) => {
    const [ref, setRef] = useState(null);

    useEffect(() => {
        if (data?.polygon !== null && ref === null) {
            setRef(data.polygon);
        }
    }, [ref]);

    const onPolygonChange = (e) => {
        if (e?.value?.id !== 0) {
            data.polygon = e.value;
        } else {
            data.polygon = null;
        }
        setRef(e.value);
        update(data);

    };

    const centerOnPolygon = () => {
        center(data);
    };

    const setReAssign = () => {
        data.reAssign = true;
        data.polygon = null;
        update(data, true);
        setRef(null);
    };

    const resetToOriginal = () => {
        data.reAssign = false;
        data.polygon = null;
        update(data, true);
        setRef(null);
    };
    return (<div className='row row-table align-items-center'>
        <div className={poligonosList?.length > 1 ? "col-12 col-md-4 text-md-left" : "col-12 col-sm-4 col-md-8 text-left"}>
            <span>{data.nombre}</span>
        </div>
        {poligonosList?.length > 1 && <div className="col-12 col-md-4 text-md-center">
            <DropdownPrime
                value={data.polygon}
                onChange={(e) => { onPolygonChange(e); }}
                options={poligonosList} optionLabel="nombre"
                name="polygon_id"
                disabled={data.originalPol !== null && !data.reAssign}
            />
        </div>}
        <div className={poligonosList?.length > 1 ? "col-12 col-md-4 text-md-right" : "col-12 col-sm-1 col-md-4 text-right"}>
            {((data?.originalPol !== null && !data?.reAssign) || data?.polygon !== null) && <span className="pointer" onClick={() => centerOnPolygon()}><FontAwesomeIcon icon={faEye} color="gray" /></span>}
            {fileUploaded && poligonosList?.length > 1 && data?.originalPol !== null && !data?.reAssign && <span className="pointer" onClick={() => setReAssign()}><FontAwesomeIcon icon={faTimesCircle} color="gray" /></span>}
            {fileUploaded && poligonosList?.length > 1 && data?.originalPol !== null && data?.reAssign && <span className="pointer" onClick={() => resetToOriginal()}><FontAwesomeIcon icon={faUndo} color="gray" /></span>}
        </div>
    </div>
    );
};

const NodesMapTableRow = ({ dataFarm, data, puntosList, update, center, fileUploaded }) => {
    const [ref, setRef] = useState(null);

    useEffect(() => {
        if (data?.point !== null && ref === null) {
            setRef(data.point);
        }
    }, [ref]);

    const onPointChange = (e) => {
        if (e?.value?.id !== 0) {
            data.point = e.value;
        } else {
            data.point = null;
        }
        setRef(e.value);
        update(data);
    };

    const centerOnPoint = () => {
        center(ref);
    };

    const setReAssign = () => {
        data.point = null;
        data.reAssign = true;
        update(data, true);
        setRef(null);
    };

    const resetToOriginal = () => {
        data.reAssign = false;
        data.point = null;
        update(data, true);
        setRef(null);
    };

    return (
        <div className='row row-table align-items-center'>
            <div className={puntosList?.length > 1 ? "col-12 col-md-4 text-md-left" : "col-12 col-sm-4 col-md-8 text-left"}>
                <span>{data.nombre}</span>
            </div>
            {puntosList?.length > 1 && <div className="col-12 col-md-4 d-flex justify-content-end align-items-center">
                <DropdownPrime
                    value={data.point}
                    onChange={(e) => { onPointChange(e); }}
                    options={puntosList} optionLabel="nombre"
                    name="point_id"
                    disabled={data.originalPnt !== null && !data.reAssign}
                />
            </div>}
            <div className={puntosList?.length > 1 ? "col-12 col-md-4 text-md-right" : "col-12 col-sm-2 col-md-4 text-right"}>
                {((data?.originalPnt !== null && !data?.reAssign) || data?.point !== null) && <span className="pointer" onClick={() => centerOnPoint()}><FontAwesomeIcon icon={faEye} color="gray" /></span>}
                {fileUploaded && puntosList?.length > 1 && data?.originalPnt !== null && !data?.reAssign && <span className="pointer" onClick={() => setReAssign()}><FontAwesomeIcon icon={faTimesCircle} color="gray" /></span>}
                {fileUploaded && puntosList?.length > 1 && data?.originalPnt !== null && data?.reAssign && <span className="pointer" onClick={() => resetToOriginal()}><FontAwesomeIcon icon={faUndo} color="gray" /></span>}
            </div>
        </div>
    );
};

const NetworkTable = ({ dataFarm, dispatch, canEdit, nodesInfo, mapRef, t, i18n }) => {
    const navigate = useNavigate();
    const [dialogState, setDialogState] = useState({ visible: false });
    const [networkNodes, setNetworkNodes] = useState(null);
    const formikRef = useRef(false);
    const treeGrid = useRef(null);
    const nodesDict = dispatch({ type: "get", path: "nodesDict" });
    const farmId = dataFarm && dataFarm.campo_id;
    const husoHorario = dataFarm.huso_horario;
    const updatingRef = useRef(false);
    const awsOptions = useRef({ t, dispatch, path: "nodes", canModify: ["nombre"] });
    const names = useMemo(() => ["ID", t("node.nodo"), t("general.type"), t("node.tipoPlan"), t("node.fechaEntrega"), t("node.conexion"), t("node.atrasado"), t("node.finalidad")], [t]);
    const canAccessComparams = dispatch({ type: "get", path: "canAccessComparams" });
    const url = `/farms/${farmId}/node`;
    if (awsOptions.current.url !== url) awsOptions.current.url = url;

    const gwAnd3g = useMemo(() => {
        if (nodesDict) {
            const cantGws = Object.values(nodesDict).filter(n => n.has_modem);
            if (cantGws.length >= 2) {
                return true;
            }
        }
    }, [nodesDict]);
    const templatesDataPointer = useRef({});
    const templateData = templatesDataPointer.current;
    if (templateData.farmId !== farmId) templateData.farmId = farmId;
    if (templateData.navigate !== navigate) templateData.navigate = navigate;
    if (templateData.gwAnd3g !== gwAnd3g) templateData.gwAnd3g = gwAnd3g;

    // eslint-disable-next-line react/display-name
    const templateRedirectToNode = useMemo(() => ({ nombre, nodo_id, parent }) => {
        if (!nombre || !nodo_id) return "-";
        return (
            <a className="pointer" onClick={() => {
                const { farmId, navigate, gwAnd3g } = templatesDataPointer.current;
                navigate({ pathname: "/farms/node", search: `?farm=${farmId}&node=${nodo_id}` },
                    {
                        state: {
                            expanded: (typeof parent === "number" && gwAnd3g) ?
                                ["network", `nodo-${parent}`, `nodo-${nodo_id}`] :
                                ["network",`nodo-${nodo_id}`]
                            , updateTree: true
                        }
                    });
            }}
            >{nombre}</a>
        );
    }, []);

    useEffect(() => {
        const newNodes = [];
        const cloneNodes = [...nodesInfo];
        Array.isArray(cloneNodes) && cloneNodes.forEach((n, i) => {
            n.plan_contrato = n.plan_contrato==null?"FREE":n.plan_contrato;
            n.fecha_entrega_formateada = n.fecha_entrega ? dateFormat(n.fecha_entrega, t, i18n.language, null, true) : "-";
            /* const tipoNodo = switchTipoNodo(n.tipo); */
            n.tipo_nodo = (!n.is_gw ? switchTipoNodo(n.tipo) : `${switchTipoNodo(n.tipo)} Gateway`);
            n.atrasado = husoHorario && n.fecha_ultimo_dato && dateDiffInMinutes(n.fecha_ultimo_dato, husoHorario) >= 180;
            n.atrasado ? n.atrasado = t("general.answerYes") : n.atrasado = "";
            const arrFinalidades = n.finalidades.map(finalidad => {
                return t(`node.tipo.${finalidad}`);
            });
            n.finalidad = arrFinalidades.join(", ");
            Array.isArray(n.subNodes) && n.subNodes.forEach((sub, j) => {
                sub.contrato = sub.tipo_contrato_id ? t(`node.plan.${sub.tipo_contrato_id}`) : "-";
                sub.fecha_entrega_formateada = sub.fecha_entrega ? dateFormat(sub.fecha_entrega, t, i18n.language, null, true) : "-";
                /*sub.tipo_nodo = (!sub.is_gw ? switchTipoNodo(sub.tipo) : `${switchTipoNodo(sub.tipo)} Gateway`);*/
                sub.tipo_nodo = (!sub.is_gw ? switchTipoNodo(sub.tipo) : `${switchTipoNodo(sub.tipo)} Gateway`);
                sub.atrasado = husoHorario && sub.fecha_ultimo_dato && dateDiffInMinutes(sub.fecha_ultimo_dato, husoHorario) >= 180;
                sub.atrasado ? sub.atrasado = t("general.answerYes") : sub.atrasado = "";
                const arrFinalidades = sub.finalidades.map(finalidad => {
                    return t(`node.tipo.${finalidad}`);
                });
                sub.finalidad = arrFinalidades.join(", ");
                sub.key = `${i}-${j}`;
                if(sub.subNodes){
                    sub.children = sub.subNodes;
                    Array.isArray(sub.children) && sub.children.forEach((sub_sub, k) => {
                        sub_sub.contrato = sub_sub.tipo_contrato_id ? t(`node.plan.${sub_sub.tipo_contrato_id}`) : "-";
                        sub_sub.fecha_entrega_formateada = sub_sub.fecha_entrega ? dateFormat(sub_sub.fecha_entrega, t, i18n.language, null, true) : "-";
                        /*sub_sub.tipo_nodo = (!sub_sub.is_gw ? switchTipoNodo(sub_sub.tipo) : `${switchTipoNodo(sub_sub.tipo)} Gateway`);*/
                        sub_sub.tipo_nodo = (!sub_sub.is_gw ? switchTipoNodo(sub_sub.tipo) : `${switchTipoNodo(sub_sub.tipo)} Gateway`);
                        sub_sub.atrasado = husoHorario && sub_sub.fecha_ultimo_dato && dateDiffInMinutes(sub_sub.fecha_ultimo_dato, husoHorario) >= 180;
                        sub_sub.atrasado ? sub_sub.atrasado = t("general.answerYes") : sub_sub.atrasado = "";
                        const arrFinalidades = sub_sub.finalidades.map(finalidad => {
                            return t(`node.tipo.${finalidad}`);
                        });
                        sub_sub.finalidad = arrFinalidades.join(", ");
                        sub_sub.key = `${i}-${j}-${k}`;
                        sub_sub.data = {...sub_sub};
                    });
                }
                sub.data = {...sub};
            });
            n.key = `${i}`;
            n.children = n.subNodes;
            n.data = {...n};
            newNodes.push(n);
        });
        setNetworkNodes(newNodes);
    }, [nodesInfo,t]);

    // eslint-disable-next-line react/display-name
    const quantityOfConnections = useMemo(() => (props) => {
        const { data, cantidad } = props?.data??{};
        const arrData = [];
        data?.tipoActuador?.forEach((actuador) => {
            const tipoAc = t(`tipo.actuador.${actuador.tipo}`);
            let subTipo;
            actuador.sub_tipo ? subTipo = t(`tipo.asociacion_ha.${actuador.sub_tipo}`, "") : subTipo = "-";
            actuador.nombre = `${tipoAc} - ${subTipo}`;
            arrData.push({ nombre: actuador.nombre, cantidad: actuador.cantidad});
        });
        const contador = {};
        data?.tipoSensor?.forEach((sensor) => {
            const tipoSen = sensor.tipo[i18n.language];
            !contador[tipoSen] ? contador[tipoSen] = 1 : contador[tipoSen]++;
        });
        for (const tipoSen in contador) {
            arrData.push({ nombre: tipoSen, cantidad: contador[tipoSen] });
        }
        let dataString = "";
        arrData.forEach((row, index) => {
            dataString += `${row.nombre}: ${row.cantidad}`;
            if (index < arrData.length - 1) {
                dataString += "\n";
            }
        });
        const tableTooltip = arrData.map((row, index) => (
            <tr key={index}>
                <td>{row.nombre}</td>
                <td style={{ paddingLeft: "10px" }}>{row.cantidad}</td>
            </tr>
        ));
        return (
            cantidad === 0 ?
                <span>{cantidad}</span>:
                <TooltipPrime id={`farmInfo_tables-link-${props?.id_externo}`} content={
                    <table>
                        <tbody>
                            {tableTooltip}
                        </tbody>
                    </table>
                } position="left"
                >
                    <a className="pointer" style={{ fontSize: "14px", textDecoration: "none", color: "inherit", fontFamily: "Montserrat" }}>{cantidad}</a>
                </TooltipPrime>
        );
    }, [t]);

    // eslint-disable-next-line react/display-name
    const templateFinalidad = useMemo(() => (props) => {
        const { finalidades } = props;
        const arrFinalidades = finalidades.map(finalidad => {
            return t(`node.tipo.${finalidad}`);
        });
        const finalidad = arrFinalidades.join(", ");
        let isLong = false;

        if (finalidad.length >= 26) {
            isLong = true;
        } else {
            isLong = false;
        }

        return isLong ?
            <TooltipPrime id={`farmInfo-Tables-purpouse-${props.externo}`} content={finalidad} position="left">
                <a className="pointer" style={{ fontSize: "14px", textDecoration: "none", color: "inherit", fontFamily: "Montserrat"}}>{finalidad}</a>
            </TooltipPrime>
            :
            (<a style={{ fontSize: "14px", textDecoration: "none", color: "inherit", fontFamily: "Montserrat"}}>{finalidad}</a>);
    }, []);

    // eslint-disable-next-line react/display-name
    const templateRetrasado = useMemo(() => (props) => {
        const { atrasado, fecha_ultimo_dato } = props;
        return (
            <TooltipPrime id={`farmInfo-Tables-lastDato-${props.id_externo}`}  content={`${t("node.ultimoDato")}: ${dateFormat(fecha_ultimo_dato, t, i18n.language, husoHorario)}`} position="left">
                <a className="pointer" style={{ fontSize: "14px", textDecoration: "none", color: "inherit", fontFamily: "Montserrat"}}>
                    {atrasado}
                </a>
            </TooltipPrime>);
    }, []);
    useTreegridColsEffect(awsOptions, treeGrid, names, t);

    const [loadingTable, setLoadingTable] = useState(false);
    if (!Array.isArray(nodesInfo)) return null;
    return (<div>
        <DialogComponentWrapper formikRef={formikRef} containerId="network-table-form"
            t={t} dialogState={dialogState} setDialogState={setDialogState}
        >
            <NodeForm
                innerRef={formikRef}
                dataNodos={dataFarm.mapa_json && dataFarm.mapa_json.nodos}
                dispatch={dispatch}
                mapRef={mapRef}
                canEdit
                edit
                isTableDialog
                farmId={farmId}
                data={dialogState.data}
                updateData={(data, previousData, key) => primeUpdate(key, data, previousData,awsOptions, networkNodes, setNetworkNodes, TREE_TABLE, setDialogState, setLoadingTable, dialogState)}
            />
        </DialogComponentWrapper>
        <TreeTablePrime
            loading={loadingTable}
            t={t}
            tableName="NodesTable"
            expandedKeys={["tipo_nodo"]}
            nodes={networkNodes}
            scrollable scrollHeight="55vh" horizontalScroll={canAccessComparams}
            paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
            header="search"
            filterMode="strict"
            editableRow={canEdit}
            editFunction= {(event, rowData) => setDialogState({ target:event.target, data: rowData, headerText: t("forms.edit"), visible: true })}
            fields={[
                {field:"nodo_id",header:names[0], hidden:true},
                {field:"nombre",header:names[1], body:templateRedirectToNode},
                {field:"tipo_nodo",header:names[2], expander:true, sortable:true},
                {field:"plan_contrato",header:names[3], hidden:!canAccessComparams, sortable:true},
                {field:"fecha_entrega_formateada",header:names[4], hidden:!canAccessComparams, sortable:true},
                {field:"cantidad",header:names[5], body:quantityOfConnections, hidden:!canAccessComparams, sortable:true},
                {field:"atrasado",header:names[6], body:templateRetrasado, hidden:!canAccessComparams, sortable:true},
                {field:"finalidad",header:names[7], body:templateFinalidad, hidden:!canAccessComparams, sortable:true}
            ]}
        />
    </div>);
};

const SensorsTableApiThird = ({ farmId, sensores, sectorsInfo, t, i18n }) => {
    const navigate = useNavigate();
    const apiTableRef = useRef(null);
    const names = useMemo(() => ["ID", t("general.name"), t("general.type"), t("farms.zone.sectorRelacionado")], [t]);

    const dataSource = useMemo(() => {
        const sens = [];
        Array.isArray(sensores) && sensores.forEach((sen, i) => {
            let sectores_relacionados = "-";
            sen && Array.isArray(sen.sectores) && sen.sectores.length && sen.sectores.forEach(sec => {
                if (sectorsInfo[sec]) {
                    sectores_relacionados = sectores_relacionados !== "-" ? `${sectores_relacionados}, ${sectorsInfo[sec].nombre}` : sectorsInfo[sec].nombre;
                } else {
                    sen.sectores = sen.sectores.filter(s => s !== sec);
                }
            });
            sen.sectores_relacionados = sectores_relacionados;
            sen.tipo_sensor = sen.tipo ? (sen.tipo[i18n.language] ?? sen.tipo["en"]) : "-";
            sen.key = `${i}`;
            sen.data = {...sen};
            sens.push(sen);
        });
        return sens;
    }, [sensores, sectorsInfo, t]);

    const templatesDataPointer = useRef({});
    const templateData = templatesDataPointer.current;
    if (templateData.farmId !== farmId) templateData.farmId = farmId;
    if (templateData.navigate !== navigate) templateData.navigate = navigate;

    const { templateRedirectToZone } = useMemo(() => {
        const templateRedirectToZone = ({ sectores_relacionados, sectores }) => {
            if (!sectores || !sectores[0]) return "-";

            return (
                <a className="pointer" onClick={() => {
                    const { farmId, navigate } = templatesDataPointer.current;
                    navigate({ pathname: "/farms/zone", search: `?farm=${farmId}&zone=${sectores[0]}` },
                        { state: { expanded: ["sectores",`sector-${sectores[0]}`], updateTree: true } });
                }} rel="noopener noreferrer"
                >
                    {sectores_relacionados}
                </a>
            );
        };
        return { templateRedirectToZone };
    }, []);

    useEffect(() => {
        if (apiTableRef.current) {
            const treeGridObj = apiTableRef.current;
            treeGridObj.setProperties({ columns: treeGridObj.columns, dataSource: dataSource }, !treeGridObj.grid);
        }
    }, [dataSource, t]);

    return (<div>

        <TreeTablePrime
            t={t}
            tableName="NodesTableSensors"
            expandedKeys={["tipo_sensor"]}
            nodes={dataSource}
            scrollable scrollHeight="55vh" horizontalScroll
            globalFilterFields={["nombre", "tipo_sensor", "sectores_relacionados"]}
            filterMode="strict"
            fields={[
                {field:"sensor_id",header:names[0], hidden:true},
                {field:"nombre",header:names[1], sortable:true},
                {field:"tipo_sensor",header:names[2], expander:true, sortable:true},
                {field:"sectores_relacionados",header:names[3], body:templateRedirectToZone, sortable:true},
            ]}
        />
    </div>);
};

const ActuatorsOrSensorsTable = ({ farmId, actuadores, sensores, t, i18n }) => {
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const names = useMemo(() => ["ID", t("general.name"), (sensores && t("general.type"))], [t]);

    const templatesDataPointer = useRef({});
    const templateData = templatesDataPointer.current;
    if (templateData.farmId !== farmId) templateData.farmId = farmId;
    if (templateData.navigate !== navigate) templateData.navigate = navigate;

    const dataSource = useMemo(() => {
        if (sensores) {
            sensores.forEach((sen, i) => {
                sen.tipo_traducido = (sen.tipo[i18n.language] ?? sen.tipo["en"]);
                sen.key = `${i}`;
                sen.data = {...sen};
            });
            return sensores;
        }
        return actuadores;
    }, [farmId, actuadores, sensores, t]);

    const { templateRedirectToAct, templateRedirectToSen } = useMemo(() => {
        const templateRedirectToAct = ({ nombre, actuador_id, parentId }) => {
            if (!nombre) return "-";
            return (
                <a className="pointer" onClick={() => {
                    const { farmId, navigate } = templatesDataPointer.current;
                    navigate({ pathname: "/farms/actuator", search: `?farm=${farmId}&actuator=${actuador_id}` },
                        { state: { expanded: ["network",`nodo-${parentId}`,`actuadores-${parentId}`,`actuador-${actuador_id}`], updateTree: true } });
                }} rel="noopener noreferrer"
                >
                    {nombre}
                </a>
            );
        };
        const templateRedirectToSen = ({ nombre, sensor_id, parentId }) => {
            if (!nombre) return "-";
            return (
                <a className="pointer" onClick={() => {
                    const { farmId, navigate } = templatesDataPointer.current;
                    navigate({ pathname: "/farms/sensor", search: `?farm=${farmId}&sensor=${sensor_id}` },
                        { state: { expanded: ["network",`nodo-${parentId}`,`sensores-${parentId}`,`sensor-${sensor_id}`], updateTree: true } });
                }} rel="noopener noreferrer"
                >
                    {nombre}
                </a>
            );
        };
        return { templateRedirectToAct, templateRedirectToSen };
    }, [farmId, sensores, actuadores, t]);

    useEffect(() => {
        if (tableRef.current) {
            const treeGridObj = tableRef.current;
            treeGridObj.setProperties({ columns: treeGridObj.columns, dataSource: dataSource }, !treeGridObj.grid);
        }
    }, [actuadores, sensores, names, t]);

    return (<div>
        <TreeTablePrime
            t={t}
            tableName="ActuatorOrSensorTable"
            nodes={dataSource}
            paginator = {dataSource?.length > 5 ? true:null}
            rows={10}
            scrollable scrollHeight="55vh" horizontalScroll
            rowsPerPageOptions={dataSource?.length > 5 ? [10, 25, 50]:null}
            header={dataSource?.length > 5 ? "search":null}
            filterMode="strict"
            fields={[
                {field:"actuador_id",header:names[0], hidden:true},
                {field:"nombre",header:names[1], body:actuadores ? templateRedirectToAct : templateRedirectToSen},
                {field:"tipo_traducido",header:names[2], hidden:!sensores},
            ]}
        />
    </div>);
};

const DownstreamUpstreamTable = ({ farmId, hidraulicas, t, tableName }) => {
    const navigate = useNavigate();
    const tableRef = useRef(null);
    const names = useMemo(() => ["ID", t("general.name"), t("farms.actuator.tipoAsociacion")], [t]);

    const templatesDataPointer = useRef({});
    const templateData = templatesDataPointer.current;
    if (templateData.farmId !== farmId) templateData.farmId = farmId;
    if (templateData.navigate !== navigate) templateData.navigate = navigate;

    const dataSource = useMemo(() => {
        Array.isArray(hidraulicas) && hidraulicas.forEach((h, i) => {
            h.key = `${i}`;
            h.data = {...h};
        });
        return hidraulicas;
    }, [farmId, hidraulicas, t]);

    const { templateRedirect } = useMemo(() => {
        const templateRedirect = ({ nombre, hidraulica_id, parentId }) => {
            if (!nombre) return "-";
            return (
                <a className="pointer" onClick={() => {
                    const { farmId, navigate } = templatesDataPointer.current;
                    navigate({ pathname: "/farms/hydraulic", search: `?farm=${farmId}&hydraulic=${hidraulica_id}` },
                        { state: { expanded: ["sectores",`sector-${parentId}`,`hidraulicas-${parentId}`, `hidraulica-${hidraulica_id}_sector-${parentId}`], updateTree: true } });
                }} rel="noopener noreferrer"
                >
                    {nombre}
                </a>
            );
        };
        return { templateRedirect };
    }, [farmId, hidraulicas]);

    useEffect(() => {
        if (tableRef.current) {
            const treeGridObj = tableRef.current;
            treeGridObj.setProperties({ columns: treeGridObj.columns, dataSource: dataSource }, !treeGridObj.grid);
        }
    }, [hidraulicas, names, t]);

    return (<div>
        <TreeTablePrime
            t={t}
            tableName={tableName}
            nodes={dataSource}
            paginator = {dataSource?.length > 5 ? true:null}
            rows={10}
            scrollable scrollHeight="55vh" horizontalScroll
            rowsPerPageOptions={dataSource?.length > 5 ? [10, 25, 50]:null}
            header={dataSource?.length > 5 ? "search":null}
            globalFilterFields={["nombre", "tipo_traducido"]}
            filterMode="strict"
            fields={[
                {field:"hidraulica_id",header:names[0], hidden:true},
                {field:"nombre",header:names[1], body:templateRedirect, sortable:true},
                {field:"tipo_traducido",header:names[2], sortable:true},
            ]}
        />
    </div>);
};

const PhenologyTable = ({ dataFarm, t, i18n }) => {
    const treeGridRef = useRef(null);
    const navigate = useNavigate();
    const promiseWrapper = useRef(null);
    const [_, dispatch] = useContext(FarmContext);

    const names = useMemo(() => [
        t("general.name"), t("cultivos.especie"), t("cultivos.variedad"), t("farms.fenologia.estadosCount"),
        t("farms.fenologia.paleta"), t("farms.fenologia.tipoRenovacion")
    ], [t]);

    // mutate table data
    const dataSource = useMemo(() => {
        return dataFarm.fenologia.map(plantilla => {
            const paletaId = dataFarm.paletasFenologia.find(p => {
                return plantilla.paleta_colores?.every((color, idx) => color === p.palette[idx]);
            })?.id || "-";
            return {
                ...plantilla,
                especie_lng: plantilla.especie?.[i18n.language] || "-",
                variedad_lng: plantilla.variedad?.[i18n.language] || "-",
                estados_count: plantilla.estados_fenologicos?.length || 0,
                paleta: t("farms.fenologia.paletaN", { id: paletaId })
            };
        });
    }, [dataFarm.fenologia, dataFarm.paletasFenologia, i18n.language, t]);

    const heightTable = Array.isArray(dataSource) &&
    (dataSource.length < 10 ? `${dataSource.length * 100}px` : "500px");

    const editPlantilla = (dataFarm, rowData) => {
        const newSearch = `?farm=${dataFarm.campo_id}&plantilla=${rowData.plantilla_estado_fenologico_id}`;
        navigate({ pathname: "/farms/phenology", search: newSearch });
    };
    const deletePlantilla = async (dataFarm, rowData, promiseWrapper) => {

        const userAcceptsDelete = await promiseWrapper.current.getPromiseAnswer();
        if (userAcceptsDelete){
            const plantillaId = rowData.plantilla_estado_fenologico_id;
            try {
                const endpoint = `/farms/${dataFarm.campo_id}/plantillaFenologia/${plantillaId}/eliminar`;
                const data = await RestAPI.post("apiAdminDropControl", endpoint);
                if (data.success){
                    const newFenologia = dataFarm.fenologia.filter(p => p.plantilla_estado_fenologico_id !== plantillaId);
                    dispatch({ type: "set", params: ds => ({ ...ds, dataFarm: { ...dataFarm, fenologia: newFenologia } }) });
                    toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                }
            } catch (error) {
                toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
            }
        }
    };
    const editOrDeletePlantilla = async (args) => {
    // EDIT plantilla
        if (args.requestType === "beginEdit"){
            args.cancel = true;
            const newSearch = `?farm=${dataFarm.campo_id}&plantilla=${args.rowData.plantilla_estado_fenologico_id}`;
            navigate({ pathname: "/farms/phenology", search: newSearch });
        }
        // DELETE plantilla
        else if (args.requestType === "delete"){
            args.cancel = true;
            const userAcceptsDelete = await promiseWrapper.current.getPromiseAnswer();
            if (userAcceptsDelete){
                const plantillaId = args.data[0].plantilla_estado_fenologico_id;
                try {
                    const endpoint = `/farms/${dataFarm.campo_id}/plantillaFenologia/${plantillaId}/eliminar`;
                    const data = await RestAPI.post("apiAdminDropControl", endpoint);
                    if (data.success){
                        const newFenologia = dataFarm.fenologia.filter(p => p.plantilla_estado_fenologico_id !== plantillaId);
                        dispatch({ type: "set", params: ds => ({ ...ds, dataFarm: { ...dataFarm, fenologia: newFenologia } }) });
                        toast.success(t("forms.toastSuccess"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                    }
                } catch (error) {
                    toast.error(t("forms.toastError"), { position: toast.POSITION.TOP_RIGHT, autoClose: 6000 });
                }
            }
        }
    };

    useTreegridColsEffect(null, treeGridRef, names, t);

    return (<div>
        <PromiseWrapper
            ref={promiseWrapper}
            yesText={t("general.answerYes")} noText={t("general.answerNo")}
            headerText={t("forms.delete")} contentText={t("farms.fenologia.deleteDialog")}
        />
        <DataTablePrime
            t={t}
            header="search"
            items={dataSource}
            scrollHeight={heightTable}
            editableRow
            editFunction = {(event, rowData) => editPlantilla(dataFarm, rowData)}
            deletableRow
            deleteFunction = {(event, rowData) => deletePlantilla(dataFarm, rowData, promiseWrapper)}
            paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
            filters={{
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            }}
            size="small"
            globalFilterFields={["especie_lng"]}
            rowDataBoundFunction= {(rowData) => {
                if (rowData.sectors) return true;
                else return false;
            }}
            rowDataBoundConstraints={{"type":"disabled","on":["editableRow"], "tooltip":t("farms.fenologia.noEditable")}}
            fields={[
                {field:"plantilla_estado_fenologico_id",header:"",hidden:true,  bodyStyle:{textAlign:"center"}},
                {field:"nombre",header:names[0]},
                {field:"especie_lng",header:names[1]},
                {field:"variedad_lng",header:names[2]},
                {field:"estados_count",header:names[3]},
                {field:"paleta",header:names[4]},
                {field:"tipo_renovacion",header:names[5], body:((rowData) =>  t(`farms.fenologia.tiposRenovacion.${rowData.tipo_renovacion}`, "-"))},
            ]}
        />
    </div>);
};

export { ZonesTable, NetworkTable, SensorsTableApiThird, ActuatorsOrSensorsTable, DownstreamUpstreamTable, ZonesMapTableRow, NodesMapTableRow,
    PhenologyTable };
